import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog.jsx';
import TemplateModal from './TemplateModal';
import { validateDateBR } from '../lib/helpers';
import {
  Users,
  Calendar,
  Target,
  CheckCircle,
  Star,
  Plus,
  Edit,
  Trash2,
  ArrowRight,
  ArrowLeft,
  PlayCircle,
  Save,
  FileText,
  Sparkles,
  Award,
  Lightbulb,
  Settings,
  Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Templates pré-configurados
const TEMPLATES_AVALIACAO = [
  {
    id: 'template_1',
    nome: 'Avaliação Semestral Padrão',
    descricao: 'Template para avaliação semestral com foco em competências gerais',
    peso_metas: 60,
    peso_competencias: 40,
    peso_gestor: 70,
    peso_autoavaliacao: 30,
    competencias: [
      { nome: 'Comunicação', descricao: 'Habilidade de se comunicar de forma clara e eficaz' },
      { nome: 'Trabalho em Equipe', descricao: 'Capacidade de colaborar e trabalhar em grupo' },
      { nome: 'Liderança', descricao: 'Capacidade de guiar e inspirar outros' },
      { nome: 'Inovação', descricao: 'Criatividade e busca por soluções inovadoras' },
      { nome: 'Comprometimento', descricao: 'Dedicação e responsabilidade com resultados' }
    ]
  },
  {
    id: 'template_2',
    nome: 'Avaliação Técnica',
    descricao: 'Template focado em competências técnicas',
    peso_metas: 70,
    peso_competencias: 30,
    peso_gestor: 80,
    peso_autoavaliacao: 20,
    competencias: [
      { nome: 'Conhecimento Técnico', descricao: 'Domínio das ferramentas e tecnologias' },
      { nome: 'Resolução de Problemas', descricao: 'Capacidade de identificar e resolver problemas' },
      { nome: 'Qualidade', descricao: 'Atenção aos detalhes e qualidade do trabalho' },
      { nome: 'Produtividade', descricao: 'Eficiência na entrega de resultados' },
      { nome: 'Aprendizado Contínuo', descricao: 'Busca constante por conhecimento' }
    ]
  },
  {
    id: 'template_3',
    nome: 'Avaliação de Liderança',
    descricao: 'Template para avaliar competências de liderança',
    peso_metas: 50,
    peso_competencias: 50,
    peso_gestor: 60,
    peso_autoavaliacao: 40,
    competencias: [
      { nome: 'Visão Estratégica', descricao: 'Capacidade de pensar a longo prazo' },
      { nome: 'Desenvolvimento de Pessoas', descricao: 'Habilidade de desenvolver talentos' },
      { nome: 'Tomada de Decisão', descricao: 'Assertividade nas decisões importantes' },
      { nome: 'Gestão de Conflitos', descricao: 'Habilidade de mediar e resolver conflitos' },
      { nome: 'Inspiração', descricao: 'Capacidade de motivar e engajar equipes' }
    ]
  }
];

const AvaliacaoDesempenhoWizard = () => {
  const [currentTab, setCurrentTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  // Wizard states
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    tipo_criacao: 'manual', // 'manual' ou 'template'
    template_selecionado: null,
    competencias_selecionadas: []
  });
  
  // Data states
  const [competencias, setCompetencias] = useState([]);
  const [ciclos, setCiclos] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [gestores, setGestores] = useState([]);
  const [templates, setTemplates] = useState([]);
  
  // Modal states
  const [modalTemplate, setModalTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [modalCicloDetalhes, setModalCicloDetalhes] = useState(false);
  const [modalEditarCiclo, setModalEditarCiclo] = useState(false);
  const [cicloSelecionado, setCicloSelecionado] = useState(null);
  const [cicloEditando, setCicloEditando] = useState(null);
  const [novoTemplate, setNovoTemplate] = useState({
    nome: '',
    descricao: '',
    peso_metas: 0,
    peso_competencias: 50,
    peso_gestor: 70,
    peso_autoavaliacao: 30,
    competencias: [],
    evaluation_types: ['gestor', 'autoavaliacao']
  });
  const [novaCompetenciaTemplate, setNovaCompetenciaTemplate] = useState({
    nome: '',
    descricao: ''
  });
  
  // Form states
  const [novaCompetencia, setNovaCompetencia] = useState({
    nome: '',
    descricao: '',
    categoria: 'comportamental'
  });
  
  const [novoCiclo, setNovoCiclo] = useState({
    nome: '',
    descricao: '',
    data_inicio: '',
    data_fim: '',
    prazo_avaliacao: '',
    prazo_aprovacao: '',
    peso_metas: 60,
    peso_competencias: 40,
    peso_gestor: 70,
    peso_autoavaliacao: 30,
    competencias_ids: []
  });

  const [novaAvaliacao, setNovaAvaliacao] = useState({
    ciclo_id: '',
    colaborador_id: '',
    tipo_avaliacao: 'gestor', // Tipo de avaliação: gestor, autoavaliacao, bifocal
    gestor_id: ''
  });

  // Carregar dados iniciais
  useEffect(() => {
    loadCompetencias();
    loadCiclos();
    loadColaboradores();
    loadGestores();
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/api/performance/templates`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };

  const handleCriarTemplate = async () => {
    try {
      if (!novoTemplate.nome || !novoTemplate.descricao) {
        toast.error('Nome e descrição são obrigatórios');
        return;
      }

      setLoading(true);
      
      const response = await axios.post(
        `${API}/api/performance/templates`,
        novoTemplate,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast.success('Template criado com sucesso!');
      setModalTemplate(false);
      resetTemplateForm();
      loadTemplates();
    } catch (error) {
      console.error('Erro ao criar template:', error);
      toast.error('Erro ao criar template');
    } finally {
      setLoading(false);
    }
  };

  const handleEditarTemplate = async () => {
    try {
      if (!editingTemplate) return;

      setLoading(true);
      
      await axios.put(
        `${API}/api/performance/templates/${editingTemplate.id}`,
        novoTemplate,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast.success('Template atualizado com sucesso!');
      setModalTemplate(false);
      setEditingTemplate(null);
      resetTemplateForm();
      loadTemplates();
    } catch (error) {
      console.error('Erro ao editar template:', error);
      toast.error('Erro ao editar template');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletarTemplate = async (templateId) => {
    if (!window.confirm('Tem certeza que deseja deletar este template?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/api/performance/templates/${templateId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast.success('Template deletado com sucesso!');
      loadTemplates();
    } catch (error) {
      console.error('Erro ao deletar template:', error);
      toast.error('Erro ao deletar template');
    }
  };

  const abrirModalTemplate = (template = null) => {
    if (template) {
      // Modo edição
      setEditingTemplate(template);
      setNovoTemplate({
        nome: template.nome,
        descricao: template.descricao,
        peso_metas: template.peso_metas,
        peso_competencias: template.peso_competencias,
        peso_gestor: template.peso_gestor,
        peso_autoavaliacao: template.peso_autoavaliacao,
        competencias: template.competencias || [],
        evaluation_types: template.evaluation_types || ['gestor', 'autoavaliacao']
      });
    } else {
      // Modo criação
      resetTemplateForm();
    }
    setModalTemplate(true);
  };

  const resetTemplateForm = () => {
    setNovoTemplate({
      nome: '',
      descricao: '',
      peso_metas: 0,
      peso_competencias: 50,
      peso_gestor: 70,
      peso_autoavaliacao: 30,
      competencias: [],
      evaluation_types: ['gestor', 'autoavaliacao']
    });
    setNovaCompetenciaTemplate({ nome: '', descricao: '' });
    setEditingTemplate(null);
  };

  // Funções para gerenciar ciclos
  const abrirDetalhesCiclo = (ciclo) => {
    setCicloSelecionado(ciclo);
    setModalCicloDetalhes(true);
  };

  const handleDeletarCiclo = async (cicloId) => {
    if (!window.confirm('Tem certeza que deseja deletar este ciclo? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(
        `${API}/api/performance/cycles/${cicloId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      toast.success('Ciclo deletado com sucesso!');
      await loadCiclos();  // Recarregar lista
      
    } catch (error) {
      console.error('Erro ao deletar ciclo:', error);
      toast.error(error.response?.data?.detail || 'Erro ao deletar ciclo');
    } finally {
      setLoading(false);
    }
  };

  const abrirEditarCiclo = (ciclo) => {
    // Preparar dados do ciclo para edição
    setCicloEditando({
      id: ciclo.id,
      nome: ciclo.nome || ciclo.name || '',
      descricao: ciclo.descricao || ciclo.description || '',
      data_inicio: ciclo.data_inicio || ciclo.start_date || '',
      data_fim: ciclo.data_fim || ciclo.end_date || '',
      prazo_avaliacao: ciclo.prazo_avaliacao || ciclo.evaluation_deadline || '',
      prazo_aprovacao: ciclo.prazo_aprovacao || ciclo.approval_deadline || '',
      peso_metas: ciclo.peso_metas || 60,
      peso_competencias: ciclo.peso_competencias || 40,
      peso_gestor: ciclo.peso_gestor || 70,
      peso_autoavaliacao: ciclo.peso_autoavaliacao || 30,
      competencias_ids: ciclo.competencias || ciclo.competencies || []
    });
    setModalEditarCiclo(true);
  };

  const handleAtualizarCiclo = async () => {
    try {
      setLoading(true);
      
      const cicloData = {
        ...cicloEditando,
        competencias: cicloEditando.competencias_ids
      };
      
      await axios.put(
        `${API}/api/performance/cycles/${cicloEditando.id}`,
        cicloData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success('Ciclo atualizado com sucesso!');
      setModalEditarCiclo(false);
      setCicloEditando(null);
      await loadCiclos();
      
    } catch (error) {
      console.error('Erro ao atualizar ciclo:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar ciclo');
    } finally {
      setLoading(false);
    }
  };

  // Funções antigas removidas - não são mais necessárias
  // Competências agora são selecionadas por ID diretamente no modal

  // Carregar dados iniciais
  useEffect(() => {
    loadCompetencias();
    loadCiclos();
    loadColaboradores();
    loadTemplates();
  }, []);

  // Recarregar ciclos quando mudar para a aba ciclos
  useEffect(() => {
    if (currentTab === 'ciclos') {
      console.log('👀 Mudou para aba Ciclos, recarregando...');
      loadCiclos();
    }
  }, [currentTab]);

  // loadInitialData function removed - now calling individual load functions directly

  const loadCompetencias = async () => {
    try {
      const response = await axios.get(`${API}/api/performance/competencies`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCompetencias(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar competências:', error);
      setCompetencias([]);
    }
  };

  const loadCiclos = async () => {
    try {
      console.log('🔄 Carregando ciclos...');
      const response = await axios.get(`${API}/api/performance/cycles`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      console.log('✅ Ciclos carregados:', response.data);
      setCiclos(response.data || []);
      toast.success(`${response.data?.length || 0} ciclo(s) carregado(s)`);
    } catch (error) {
      console.error('❌ Erro ao carregar ciclos:', error);
      console.error('Detalhes:', error.response?.data);
      setCiclos([]);
      toast.error('Erro ao carregar ciclos. Verifique o console.');
    }
  };

  const loadColaboradores = async () => {
    try {
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setColaboradores(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
      setColaboradores([]);
    }
  };

  const loadGestores = async () => {
    try {
      const response = await axios.get(`${API}/api/employees/gestores`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setGestores(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar gestores:', error);
      setGestores([]);
    }
  };

  // ========== COMPETÊNCIAS ==========
  const handleSaveCompetencia = async (e) => {
    e.preventDefault();
    
    if (!novaCompetencia.nome.trim()) {
      toast.error('Nome da competência é obrigatório');
      return;
    }

    try {
      setLoading(true);
      
      const response = await axios.post(
        `${API}/api/performance/competencies`,
        {
          name: novaCompetencia.nome,
          description: novaCompetencia.descricao,
          category: novaCompetencia.categoria
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success('Competência cadastrada com sucesso!');
      setNovaCompetencia({ nome: '', descricao: '', categoria: 'comportamental' });
      await loadCompetencias();
      
    } catch (error) {
      console.error('Erro ao salvar competência:', error);
      const errorDetail = error.response?.data?.detail;
      let errorMsg = 'Erro ao salvar competência';
      if (Array.isArray(errorDetail)) {
        errorMsg = errorDetail.map(e => e.msg).join('; ');
      } else if (typeof errorDetail === 'string') {
        errorMsg = errorDetail;
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCompetencia = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta competência?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/performance/competencies/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Competência excluída com sucesso!');
      await loadCompetencias();
    } catch (error) {
      console.error('Erro ao excluir competência:', error);
      toast.error('Erro ao excluir competência');
    }
  };

  // ========== WIZARD ==========
  const iniciarWizard = (tipo) => {
    setWizardData({
      tipo_criacao: tipo,
      template_selecionado: null,
      competencias_selecionadas: []
    });
    setWizardOpen(true);
    setWizardStep(1);
  };

  const aplicarTemplate = (template) => {
    // Buscar as competências completas pelos IDs do template
    const competenciasDotTemplate = template.competencias || [];
    const competenciasSelecionadas = competencias.filter(comp => 
      competenciasDotTemplate.includes(comp.id)
    );
    
    setWizardData(prev => ({
      ...prev,
      template_selecionado: template,
      competencias_selecionadas: competenciasSelecionadas  // Pré-selecionar competências do template
    }));
    
    setNovoCiclo(prev => ({
      ...prev,
      peso_metas: template.peso_metas,
      peso_competencias: template.peso_competencias,
      peso_gestor: template.peso_gestor,
      peso_autoavaliacao: template.peso_autoavaliacao
    }));
    
    const mensagem = competenciasSelecionadas.length > 0
      ? `Template "${template.nome}" aplicado com ${competenciasSelecionadas.length} competência(s) pré-selecionada(s)!`
      : `Template "${template.nome}" aplicado! Selecione as competências.`;
    
    toast.success(mensagem);
  };
  
  // Helper para buscar nome da competência por ID
  const getCompetenciaNome = (competenciaId) => {
    const comp = competencias.find(c => c.id === competenciaId);
    return comp ? comp.name : 'Competência não encontrada';
  };

  const proximoPassoWizard = () => {
    if (wizardStep === 1 && wizardData.tipo_criacao === 'template' && !wizardData.template_selecionado) {
      toast.error('Selecione um template para continuar');
      return;
    }
    
    if (wizardStep === 2 && wizardData.competencias_selecionadas.length === 0) {
      toast.error('Selecione pelo menos uma competência');
      return;
    }
    
    setWizardStep(prev => prev + 1);
  };

  const voltarPassoWizard = () => {
    setWizardStep(prev => prev - 1);
  };

  const finalizarWizard = async () => {
    await handleCreateCiclo();
    setWizardOpen(false);
    setWizardStep(1);
  };

  // ========== CICLOS ==========
  const handleCreateCiclo = async (e) => {
    if (e) e.preventDefault();
    
    if (!novoCiclo.nome || !novoCiclo.data_inicio || !novoCiclo.data_fim) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (!validateDateBR(novoCiclo.data_inicio, { pastYears: 15, futureYears: 15 }) || !validateDateBR(novoCiclo.data_fim, { pastYears: 15, futureYears: 15 })) {
      toast.error('Data de início ou fim inválida');
      return;
    }

    if (novoCiclo.data_fim < novoCiclo.data_inicio) {
      toast.error('A data fim não pode ser menor que a data início');
      return;
    }

    try {
      setLoading(true);
      
      // Preparar competências do wizard ou do formulário direto
      let competencias_para_ciclo = [];
      
      if (wizardOpen && wizardData.competencias_selecionadas.length > 0) {
        // Competências do wizard - usar IDs das competências já existentes
        // NÃO criar novas competências, apenas usar as selecionadas
        for (const comp of wizardData.competencias_selecionadas) {
          if (comp.id) {
            competencias_para_ciclo.push(comp.id);
          } else {
            console.warn('⚠️ Competência sem ID encontrada:', comp);
            // Ignorar competências sem ID do template
            // O template define apenas nomes, mas o ciclo deve usar competências existentes
          }
        }
      } else {
        competencias_para_ciclo = novoCiclo.competencias_ids;
      }
      
      // Validar que temos competências
      if (competencias_para_ciclo.length === 0) {
        toast.error('Selecione pelo menos uma competência para o ciclo');
        setLoading(false);
        return;
      }
      
      const cicloData = {
        ...novoCiclo,
        competencias: competencias_para_ciclo
      };
      
      console.log('📤 Enviando dados do ciclo:', cicloData);
      
      // Verificar token antes de enviar
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Você não está autenticado. Por favor, faça login novamente.');
        return;
      }
      
      console.log('🔑 Token disponível:', token ? 'Sim' : 'Não');
      
      const response = await axios.post(
        `${API}/api/performance/cycles`,
        cicloData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('✅ Ciclo criado:', response.data);
      toast.success('Ciclo criado com sucesso! Atualizando lista...');
      
      setNovoCiclo({
        nome: '',
        descricao: '',
        data_inicio: '',
        data_fim: '',
        prazo_avaliacao: '',
        prazo_aprovacao: '',
        peso_metas: 60,
        peso_competencias: 40,
        peso_gestor: 70,
        peso_autoavaliacao: 30,
        competencias_ids: []
      });
      
      await loadCiclos();
      await loadCompetencias();
      
      // Mudar para aba de ciclos para ver o ciclo criado
      setTimeout(() => {
        setCurrentTab('ciclos');
        toast.success('Ciclo criado! Vá para a aba "Ciclos" para visualizar.');
      }, 1000);
      
    } catch (error) {
      console.error('❌ Erro ao criar ciclo:', error);
      console.error('Detalhes completos do erro:', error.response);
      
      // Tratar diferentes tipos de erro
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Por favor, faça login novamente.');
      } else if (error.response?.status === 422) {
        // Erro de validação - detail pode ser array ou string
        const detail = error.response?.data?.detail;
        console.log('📋 Detalhes de validação:', detail);
        
        if (Array.isArray(detail)) {
          // Extrair mensagens de erro do array COM os campos
          const errorMessages = detail.map(err => {
            const field = err.loc ? err.loc.join('.') : 'campo desconhecido';
            const msg = err.msg || 'erro desconhecido';
            return `${field}: ${msg}`;
          }).join('\n');
          
          console.log('🚨 Campos com erro:', errorMessages);
          toast.error(`Erro de validação:\n${errorMessages}`);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error('Erro de validação. Verifique os dados do ciclo.');
        }
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Erro ao criar ciclo. Verifique o console.';
        toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erro ao criar ciclo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleCompetenciaWizard = (competencia) => {
    setWizardData(prev => {
      const existe = prev.competencias_selecionadas.find(c => 
        (c.id && c.id === competencia.id) || (c.nome === competencia.nome)
      );
      
      if (existe) {
        return {
          ...prev,
          competencias_selecionadas: prev.competencias_selecionadas.filter(c => 
            (c.id && c.id !== competencia.id) && (c.nome !== competencia.nome)
          )
        };
      } else {
        return {
          ...prev,
          competencias_selecionadas: [...prev.competencias_selecionadas, competencia]
        };
      }
    });
  };

  const isCompetenciaSelecionada = (competencia) => {
    return wizardData.competencias_selecionadas.some(c => 
      (c.id && c.id === competencia.id) || (c.nome === competencia.nome)
    );
  };

  // ========== CRIAR AVALIAÇÃO ==========
  const handleCriarAvaliacao = async () => {
    console.log('🔵 Botão Criar Avaliação clicado!');
    console.log('Estado novaAvaliacao:', novaAvaliacao);
    
    if (!novaAvaliacao.ciclo_id) {
      console.log('❌ Ciclo não selecionado');
      toast.error('Selecione um ciclo de avaliação');
      return;
    }

    if (!novaAvaliacao.colaborador_id) {
      console.log('❌ Colaborador não selecionado');
      toast.error('Selecione um colaborador');
      return;
    }
    
    // Validar gestor_id apenas para tipos 'gestor' e 'bifocal'
    if ((novaAvaliacao.tipo_avaliacao === 'gestor' || novaAvaliacao.tipo_avaliacao === 'bifocal') && !novaAvaliacao.gestor_id) {
      console.log('❌ Gestor não selecionado');
      toast.error('Selecione um gestor avaliador');
      return;
    }

    try {
      setLoading(true);
      
      const avaliacaoData = {
        cycle_id: novaAvaliacao.ciclo_id,
        employee_id: novaAvaliacao.colaborador_id,
        evaluation_type: novaAvaliacao.tipo_avaliacao, // Novo campo
        evaluator_id: novaAvaliacao.gestor_id || null,  // Pode ser null para autoavaliacao
        responses: [],  // Array vazio para criar avaliação pendente
        overall_comments: '',
        development_priorities: []
      };

      console.log('📤 Criando avaliação:', avaliacaoData);

      const response = await axios.post(
        `${API}/api/performance/evaluations`,
        avaliacaoData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('✅ Avaliação criada:', response.data);
      
      // Mensagens diferentes por tipo
      if (novaAvaliacao.tipo_avaliacao === 'gestor') {
        toast.success('Avaliação do Gestor criada com sucesso! Notificação enviada.');
      } else if (novaAvaliacao.tipo_avaliacao === 'autoavaliacao') {
        toast.success('Autoavaliação criada com sucesso! Colaborador será notificado.');
      } else if (novaAvaliacao.tipo_avaliacao === 'bifocal') {
        toast.success('Avaliação Bifocal criada com sucesso! Colaborador fará self-assessment primeiro.');
      }
      
      // Limpar formulário
      setNovaAvaliacao({
        ciclo_id: '',
        colaborador_id: '',
        tipo_avaliacao: 'gestor',
        gestor_id: ''
      });

    } catch (error) {
      console.error('❌ Erro ao criar avaliação:', error);
      console.error('Detalhes:', error.response?.data);
      
      // Tratar diferentes tipos de erro
      let errorMessage = 'Erro ao criar avaliação. Verifique o console.';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Se for um array de erros de validação do Pydantic
        if (Array.isArray(errorData)) {
          errorMessage = errorData.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // Se for um objeto com campo 'detail'
        else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // Se 'detail' for um array
        else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => {
            if (typeof err === 'string') return err;
            if (err.msg) return err.msg;
            return JSON.stringify(err);
          }).join(', ');
        }
        // Se for outro tipo de objeto
        else if (typeof errorData === 'object') {
          errorMessage = JSON.stringify(errorData);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ========== RENDER WIZARD ==========
  const renderWizard = () => {
    if (!wizardOpen) return null;

    const progressValue = (wizardStep / 4) * 100;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <CardHeader>
            <div className="flex items-center justify-between mb-4">
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-purple-600" />
                Assistente de Criação de Ciclo
              </CardTitle>
              <Button variant="ghost" onClick={() => setWizardOpen(false)}>
                ✕
              </Button>
            </div>
            <Progress value={progressValue} className="h-2" />
            <p className="text-sm text-gray-600 mt-2">
              Passo {wizardStep} de 4
            </p>
          </CardHeader>

          <CardContent>
            {/* PASSO 1: Escolher tipo de criação */}
            {wizardStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Como você deseja criar o ciclo?</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card 
                      className={`cursor-pointer border-2 transition-all ${
                        wizardData.tipo_criacao === 'template' 
                          ? 'border-purple-600 bg-purple-50' 
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                      onClick={() => setWizardData(prev => ({ ...prev, tipo_criacao: 'template' }))}
                    >
                      <CardContent className="pt-6">
                        <Sparkles className="w-12 h-12 text-purple-600 mb-3" />
                        <h4 className="font-semibold mb-2">Usar Template</h4>
                        <p className="text-sm text-gray-600">
                          Utilize um template pré-configurado com competências e pesos já definidos
                        </p>
                      </CardContent>
                    </Card>

                    <Card 
                      className={`cursor-pointer border-2 transition-all ${
                        wizardData.tipo_criacao === 'manual' 
                          ? 'border-blue-600 bg-blue-50' 
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                      onClick={() => setWizardData(prev => ({ ...prev, tipo_criacao: 'manual' }))}
                    >
                      <CardContent className="pt-6">
                        <Settings className="w-12 h-12 text-blue-600 mb-3" />
                        <h4 className="font-semibold mb-2">Criar Manualmente</h4>
                        <p className="text-sm text-gray-600">
                          Configure tudo do zero, escolhendo suas próprias competências e pesos
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Templates disponíveis */}
                {wizardData.tipo_criacao === 'template' && (
                  <div className="space-y-4">
                    <h4 className="font-medium">Selecione um Template:</h4>
                    {templates.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>Carregando templates...</p>
                      </div>
                    ) : (
                      templates.map(template => (
                        <Card 
                          key={template.id}
                          className={`cursor-pointer border-2 transition-all ${
                            wizardData.template_selecionado?.id === template.id
                              ? 'border-purple-600 bg-purple-50'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                          onClick={() => aplicarTemplate(template)}
                        >
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h5 className="font-semibold">{template.nome}</h5>
                                  {template.is_custom && (
                                    <Badge variant="secondary" className="text-xs">
                                      <Star className="w-3 h-3 mr-1" />
                                      Personalizado
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 mb-3">{template.descricao}</p>
                                
                                <div className="flex gap-4 text-xs text-gray-500">
                                  <span>Metas: {template.peso_metas}%</span>
                                  <span>Competências: {template.peso_competencias}%</span>
                                  <span>Gestor: {template.peso_gestor}%</span>
                                  <span>Auto: {template.peso_autoavaliacao}%</span>
                                </div>
                                
                                {template.competencias && template.competencias.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-xs text-gray-500 mb-1">Competências incluídas:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {template.competencias.map((compId, idx) => (
                                        <Badge key={idx} variant="outline" className="text-xs">
                                          {getCompetenciaNome(compId)}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              {wizardData.template_selecionado?.id === template.id && (
                                <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0 ml-2" />
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                )}

                <div className="flex justify-end">
                  <Button onClick={proximoPassoWizard}>
                    Próximo
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* PASSO 2: Selecionar competências */}
            {wizardStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Selecione as Competências</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Escolha as competências que serão avaliadas neste ciclo
                  </p>
                </div>

                {wizardData.tipo_criacao === 'template' && wizardData.template_selecionado && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-purple-900">
                      <strong>Template selecionado:</strong> {wizardData.template_selecionado.nome}
                    </p>
                    <p className="text-xs text-purple-700 mt-1">
                      O template sugere {wizardData.template_selecionado.competencias?.length || 0} competências. 
                      Selecione abaixo as competências cadastradas que deseja incluir.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
                  {/* Competências cadastradas no sistema */}
                  {competencias.length > 0 ? (
                    <>
                      <p className="text-sm font-medium text-gray-700">Competências Disponíveis:</p>
                      {competencias.map(comp => (
                        <Card 
                          key={comp.id}
                          className={`cursor-pointer border-2 transition-all ${
                            isCompetenciaSelecionada(comp)
                              ? 'border-blue-600 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300'
                          }`}
                          onClick={() => toggleCompetenciaWizard(comp)}
                        >
                          <CardContent className="pt-4 pb-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h5 className="font-medium">{comp.name}</h5>
                                {comp.description && (
                                  <p className="text-sm text-gray-600">{comp.description}</p>
                                )}
                              </div>
                              {isCompetenciaSelecionada(comp) && (
                                <CheckCircle className="w-5 h-5 text-blue-600" />
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>Nenhuma competência cadastrada. Cadastre competências primeiro.</p>
                    </div>
                  )}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-900">
                    <strong>{wizardData.competencias_selecionadas.length}</strong> competência(s) selecionada(s)
                  </p>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={voltarPassoWizard}>
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Voltar
                  </Button>
                  <Button onClick={proximoPassoWizard}>
                    Próximo
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* PASSO 3: Configurar pesos */}
            {wizardStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Configure os Pesos</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Defina a importância de cada elemento na avaliação
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Peso das Metas (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={novoCiclo.peso_metas}
                      onChange={(e) => setNovoCiclo(prev => ({
                        ...prev,
                        peso_metas: Number(e.target.value),
                        peso_competencias: 100 - Number(e.target.value)
                      }))}
                    />
                    <p className="text-xs text-gray-500">Quanto valem as metas na avaliação</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Peso das Competências (%)</Label>
                    <Input
                      type="number"
                      value={novoCiclo.peso_competencias}
                      disabled
                    />
                    <p className="text-xs text-gray-500">Calculado automaticamente</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Peso Avaliação Gestor (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={novoCiclo.peso_gestor}
                      onChange={(e) => setNovoCiclo(prev => ({
                        ...prev,
                        peso_gestor: Number(e.target.value),
                        peso_autoavaliacao: 100 - Number(e.target.value)
                      }))}
                    />
                    <p className="text-xs text-gray-500">Peso da avaliação do gestor</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Peso Autoavaliação (%)</Label>
                    <Input
                      type="number"
                      value={novoCiclo.peso_autoavaliacao}
                      disabled
                    />
                    <p className="text-xs text-gray-500">Calculado automaticamente</p>
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={voltarPassoWizard}>
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Voltar
                  </Button>
                  <Button onClick={proximoPassoWizard}>
                    Próximo
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* PASSO 4: Dados do ciclo */}
            {wizardStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Dados do Ciclo</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Preencha as informações básicas do ciclo de avaliação
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="nome">Nome do Ciclo *</Label>
                    <Input
                      id="nome"
                      value={novoCiclo.nome}
                      onChange={(e) => setNovoCiclo(prev => ({ ...prev, nome: e.target.value }))}
                      placeholder="Ex: Avaliação de Desempenho 2025"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="descricao">Descrição</Label>
                    <Textarea
                      id="descricao"
                      value={novoCiclo.descricao}
                      onChange={(e) => setNovoCiclo(prev => ({ ...prev, descricao: e.target.value }))}
                      placeholder="Descrição opcional do ciclo"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="data_inicio">Data de Início *</Label>
                      <Input
                        id="data_inicio"
                        type="date"
                        value={novoCiclo.data_inicio}
                        onChange={(e) => setNovoCiclo(prev => ({ ...prev, data_inicio: e.target.value }))}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="data_fim">Data de Fim *</Label>
                      <Input
                        id="data_fim"
                        type="date"
                        value={novoCiclo.data_fim}
                        onChange={(e) => setNovoCiclo(prev => ({ ...prev, data_fim: e.target.value }))}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="prazo_avaliacao">Prazo para Avaliação *</Label>
                      <Input
                        id="prazo_avaliacao"
                        type="date"
                        value={novoCiclo.prazo_avaliacao}
                        onChange={(e) => setNovoCiclo(prev => ({ ...prev, prazo_avaliacao: e.target.value }))}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="prazo_aprovacao">Prazo para Aprovação *</Label>
                      <Input
                        id="prazo_aprovacao"
                        type="date"
                        value={novoCiclo.prazo_aprovacao}
                        onChange={(e) => setNovoCiclo(prev => ({ ...prev, prazo_aprovacao: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Resumo */}
                <Card className="bg-green-50 border-green-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Resumo do Ciclo</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div><strong>Competências:</strong> {wizardData.competencias_selecionadas.length}</div>
                    <div><strong>Pesos:</strong> {novoCiclo.peso_metas}% Metas / {novoCiclo.peso_competencias}% Competências</div>
                    <div><strong>Avaliação:</strong> {novoCiclo.peso_gestor}% Gestor / {novoCiclo.peso_autoavaliacao}% Auto</div>
                  </CardContent>
                </Card>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={voltarPassoWizard}>
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Voltar
                  </Button>
                  <Button onClick={finalizarWizard} disabled={loading}>
                    {loading ? 'Criando...' : 'Criar Ciclo'}
                    <CheckCircle className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Avaliação de Desempenho com Assistente</h1>
        <p className="text-gray-600 mt-2">
          Sistema completo de avaliação com wizard guiado e templates pré-configurados
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={currentTab} onValueChange={setCurrentTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="competencias">Competências</TabsTrigger>
          <TabsTrigger value="ciclos">Ciclos</TabsTrigger>
          <TabsTrigger value="avaliar">Criar Avaliação</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Target className="w-5 h-5 text-blue-600" />
                  Competências
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">{competencias.length}</div>
                <p className="text-sm text-gray-600 mt-1">Cadastradas no sistema</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Calendar className="w-5 h-5 text-green-600" />
                  Ciclos Ativos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">
                  {ciclos.filter(c => c.status === 'active').length}
                </div>
                <p className="text-sm text-gray-600 mt-1">Em andamento</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="w-5 h-5 text-purple-600" />
                  Colaboradores
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600">{colaboradores.length}</div>
                <p className="text-sm text-gray-600 mt-1">Para avaliar</p>
              </CardContent>
            </Card>
          </div>

          {/* Alerta de Ajuda */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="bg-purple-600 rounded-full p-3">
                <Lightbulb className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  🧙‍♂️ Como usar o Assistente (Wizard)?
                </h3>
                <p className="text-gray-700 mb-3">
                  Clique em um dos botões abaixo para abrir o <strong>Wizard de 4 passos</strong> que vai te guiar na criação do ciclo:
                </p>
                <ul className="text-sm text-gray-600 space-y-1 mb-3">
                  <li>✅ Passo 1: Escolher Template ou Manual</li>
                  <li>✅ Passo 2: Selecionar Competências</li>
                  <li>✅ Passo 3: Configurar Pesos</li>
                  <li>✅ Passo 4: Preencher Dados do Ciclo</li>
                </ul>
                <p className="text-xs text-gray-500">
                  💡 Dica: Se for sua primeira vez, use o <strong>Template</strong> (botão roxo)
                </p>
              </div>
            </div>
          </div>

          {/* Ações Rápidas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PlayCircle className="w-5 h-5 text-purple-600" />
                🧙 Abrir Wizard (Assistente Guiado)
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button 
                onClick={() => iniciarWizard('template')}
                className="h-auto py-6 flex-col gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                <Sparkles className="w-8 h-8" />
                <span className="text-lg font-semibold">Criar com Template</span>
                <span className="text-xs opacity-90">Use um template pré-configurado</span>
              </Button>

              <Button 
                onClick={() => iniciarWizard('manual')}
                className="h-auto py-6 flex-col gap-2 bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700"
              >
                <Settings className="w-8 h-8" />
                <span className="text-lg font-semibold">Criar Manualmente</span>
                <span className="text-xs opacity-90">Configure tudo do zero</span>
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Competências Tab */}
        <TabsContent value="competencias" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Formulário */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Nova Competência
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveCompetencia} className="space-y-4">
                  <div>
                    <Label htmlFor="nome-comp">Nome *</Label>
                    <Input
                      id="nome-comp"
                      value={novaCompetencia.nome}
                      onChange={(e) => setNovaCompetencia(prev => ({ ...prev, nome: e.target.value }))}
                      placeholder="Ex: Liderança"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="descricao-comp">Descrição</Label>
                    <Textarea
                      id="descricao-comp"
                      value={novaCompetencia.descricao}
                      onChange={(e) => setNovaCompetencia(prev => ({ ...prev, descricao: e.target.value }))}
                      placeholder="Descrição da competência"
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label htmlFor="categoria">Categoria</Label>
                    <select
                      id="categoria"
                      value={novaCompetencia.categoria}
                      onChange={(e) => setNovaCompetencia(prev => ({ ...prev, categoria: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="comportamental">Comportamental</option>
                      <option value="tecnica">Técnica</option>
                      <option value="lideranca">Liderança</option>
                    </select>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? 'Salvando...' : 'Salvar Competência'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Lista */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Competências Cadastradas ({competencias.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {competencias.length === 0 ? (
                  <div className="text-center py-12">
                    <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Nenhuma competência cadastrada ainda</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Crie competências para usar em seus ciclos de avaliação
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {competencias.map(comp => (
                      <Card key={comp.id} className="border border-gray-200">
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold">{comp.name}</h4>
                                <Badge variant="outline" className="text-xs">
                                  {comp.category || 'Geral'}
                                </Badge>
                              </div>
                              {comp.description && (
                                <p className="text-sm text-gray-600">{comp.description}</p>
                              )}
                            </div>
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCompetencia(comp.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Ciclos Tab */}
        <TabsContent value="ciclos" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Ciclos de Avaliação</CardTitle>
                  <CardDescription>
                    Gerencie seus ciclos de avaliação de desempenho
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={loadCiclos} disabled={loading}>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Atualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-500">Carregando ciclos...</p>
                </div>
              ) : ciclos.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">Nenhum ciclo criado ainda</p>
                  <p className="text-xs text-gray-400 mb-4">
                    (Se você acabou de criar um ciclo, clique em "Atualizar" acima)
                  </p>
                  <Button onClick={() => iniciarWizard('template')}>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Criar Primeiro Ciclo
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {ciclos.map(ciclo => {
                    // Suporte para ambas estruturas de dados (PT e EN)
                    const nome = ciclo.nome || ciclo.name || 'Ciclo sem nome';
                    const descricao = ciclo.descricao || ciclo.description;
                    const dataInicio = ciclo.data_inicio || ciclo.start_date || 'N/A';
                    const dataFim = ciclo.data_fim || ciclo.end_date || 'N/A';
                    const pesoMetas = ciclo.peso_metas || ciclo.peso_metas || 60;
                    const pesoCompetencias = ciclo.peso_competencias || ciclo.peso_competencias || 40;
                    
                    return (
                      <Card key={ciclo.id} className="border-l-4 border-l-blue-600">
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-lg">{nome}</h4>
                                <Badge>{ciclo.status || 'Ativo'}</Badge>
                              </div>
                              {descricao && (
                                <p className="text-sm text-gray-600 mb-3">{descricao}</p>
                              )}
                              <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                                <span>📅 Início: {dataInicio}</span>
                                <span>📅 Fim: {dataFim}</span>
                                <span>📊 {pesoMetas}% Metas / {pesoCompetencias}% Competências</span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => abrirDetalhesCiclo(ciclo)}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Ver Detalhes
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => abrirEditarCiclo(ciclo)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Editar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeletarCiclo(ciclo.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Excluir
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>



        {/* Criar Avaliação Tab */}
        <TabsContent value="avaliar" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Edit className="w-6 h-6 text-blue-600" />
                Criar Nova Avaliação
              </CardTitle>
              <CardDescription>
                Crie uma nova avaliação de desempenho para um colaborador
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Formulário de Criação de Avaliação - Reorganizado */}
                <div className="space-y-4">
                  {/* PRIMEIRO CAMPO: Tipo de Avaliação */}
                  <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                    <Label htmlFor="tipo-avaliacao" className="text-base font-semibold">
                      1. Tipo de Avaliação *
                    </Label>
                    <select
                      id="tipo-avaliacao"
                      className="w-full px-3 py-2 border-2 border-blue-400 rounded-md mt-2 font-medium"
                      value={novaAvaliacao.tipo_avaliacao}
                      onChange={(e) => {
                        const novoTipo = e.target.value;
                        setNovaAvaliacao(prev => ({ 
                          ...prev, 
                          tipo_avaliacao: novoTipo,
                          // Limpar gestor_id se mudar para autoavaliacao
                          gestor_id: novoTipo === 'autoavaliacao' ? '' : prev.gestor_id
                        }));
                      }}
                    >
                      <option value="gestor">Avaliação do Gestor</option>
                      <option value="autoavaliacao">Autoavaliação</option>
                      <option value="bifocal">Avaliação Bifocal</option>
                    </select>
                    <div className="mt-2 p-2 bg-white rounded border border-blue-200">
                      <p className="text-sm text-blue-800 font-medium">
                        {novaAvaliacao.tipo_avaliacao === 'gestor' && '→ RH cria, Gestor preenche, Colaborador visualiza'}
                        {novaAvaliacao.tipo_avaliacao === 'autoavaliacao' && '→ RH cria, Colaborador preenche, Gestor visualiza'}
                        {novaAvaliacao.tipo_avaliacao === 'bifocal' && '→ RH cria, Colaborador auto-avalia, Gestor avalia, Sistema compara'}
                      </p>
                    </div>
                  </div>

                  {/* SEGUNDO CAMPO: Ciclo de Avaliação */}
                  <div>
                    <Label htmlFor="ciclo-avaliacao">2. Ciclo de Avaliação *</Label>
                    <select
                      id="ciclo-avaliacao"
                      className="w-full px-3 py-2 border rounded-md"
                      disabled={ciclos.length === 0}
                      value={novaAvaliacao.ciclo_id}
                      onChange={(e) => setNovaAvaliacao(prev => ({ ...prev, ciclo_id: e.target.value }))}
                    >
                      <option value="">Selecione um ciclo</option>
                      {ciclos.map(ciclo => (
                        <option key={ciclo.id} value={ciclo.id}>
                          {ciclo.nome || ciclo.name || 'Ciclo sem nome'} - {ciclo.status || 'Ativo'}
                        </option>
                      ))}
                    </select>
                    {ciclos.length === 0 && (
                      <p className="text-xs text-red-600 mt-1">
                        Você precisa criar um ciclo primeiro. Vá para a aba "Ciclos".
                      </p>
                    )}
                  </div>

                  {/* TERCEIRO CAMPO: Colaborador */}
                  <div>
                    <Label htmlFor="colaborador-avaliacao">3. Colaborador a Avaliar *</Label>
                    <select
                      id="colaborador-avaliacao"
                      className="w-full px-3 py-2 border rounded-md"
                      disabled={colaboradores.length === 0}
                      value={novaAvaliacao.colaborador_id}
                      onChange={(e) => setNovaAvaliacao(prev => ({ ...prev, colaborador_id: e.target.value }))}
                    >
                      <option value="">Selecione um colaborador</option>
                      {colaboradores.map(colab => (
                        <option key={colab.id} value={colab.id}>
                          {colab.nome || colab.name || 'Sem nome'}
                        </option>
                      ))}
                    </select>
                    {colaboradores.length === 0 && (
                      <p className="text-xs text-red-600 mt-1">
                        Nenhum colaborador encontrado no sistema.
                      </p>
                    )}
                  </div>
                  
                  {/* QUARTO CAMPO CONDICIONAL: Gestor Avaliador - apenas para tipos 'gestor' e 'bifocal' */}
                  {(novaAvaliacao.tipo_avaliacao === 'gestor' || novaAvaliacao.tipo_avaliacao === 'bifocal') && (
                    <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-4">
                      <Label htmlFor="gestor-avaliador" className="text-base font-semibold">
                        4. Gestor Avaliador *
                      </Label>
                      <select
                        id="gestor-avaliador"
                        className="w-full px-3 py-2 border-2 border-purple-400 rounded-md mt-2"
                        disabled={gestores.length === 0}
                        value={novaAvaliacao.gestor_id || ''}
                        onChange={(e) => setNovaAvaliacao(prev => ({ ...prev, gestor_id: e.target.value }))}
                      >
                        <option value="">Selecione um gestor</option>
                        {gestores.map(gestor => (
                          <option key={gestor.id} value={gestor.id}>
                            {gestor.nome || gestor.name || 'Sem nome'} {gestor.cargo ? `(${gestor.cargo})` : ''}
                          </option>
                        ))}
                      </select>
                      {gestores.length === 0 && !loading && (
                        <p className="text-xs text-red-500 mt-1">Nenhum gestor encontrado. Verifique se há colaboradores com perfil de gestor.</p>
                      )}
                      <p className="text-xs text-purple-700 mt-2 font-medium">
                        ℹ️ Gestor que realizará a avaliação (será notificado por email e WhatsApp)
                      </p>
                    </div>
                  )}
                </div>

                {/* Mensagem Informativa */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-900 mb-1">Como Funciona</h4>
                      <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                        <li>Selecione o ciclo de avaliação ativo</li>
                        <li>Escolha o colaborador que será avaliado</li>
                        <li>Defina o tipo de avaliação:
                          <ul className="ml-6 mt-1 space-y-0.5 list-disc">
                            <li><strong>Gestor:</strong> Gestor preenche, colaborador visualiza</li>
                            <li><strong>Autoavaliação:</strong> Colaborador preenche, gestor visualiza</li>
                            <li><strong>Bifocal:</strong> Colaborador auto-avalia, gestor avalia, sistema compara</li>
                          </ul>
                        </li>
                        <li>Selecione o gestor avaliador (para Gestor e Bifocal)</li>
                        <li>Clique em "Criar Avaliação" para gerar</li>
                        <li>O responsável receberá notificação para preencher</li>
                      </ol>
                    </div>
                  </div>
                </div>

                {/* Botões */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                    disabled={ciclos.length === 0 || colaboradores.length === 0 || loading}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCriarAvaliacao();
                    }}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Criando...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>Criar Avaliação</span>
                      </>
                    )}
                  </button>
                  
                  <Button
                    variant="outline"
                    onClick={() => setCurrentTab('ciclos')}
                  >
                    Ir para Ciclos
                  </Button>
                </div>

                {/* Preview das Competências do Ciclo */}
                {ciclos.length > 0 && (
                  <Card className="bg-gray-50">
                    <CardHeader>
                      <CardTitle className="text-sm">Competências que serão avaliadas</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs text-gray-600 mb-2">
                        As competências abaixo são do ciclo selecionado e serão incluídas na avaliação:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {competencias.slice(0, 5).map(comp => (
                          <Badge key={comp.id} variant="outline">
                            {comp.name}
                          </Badge>
                        ))}
                        {competencias.length > 5 && (
                          <Badge variant="outline">
                            +{competencias.length - 5} mais
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-6 h-6 text-purple-600" />
                    Templates Pré-configurados
                  </CardTitle>
                  <CardDescription>
                    Utilize templates prontos para acelerar a criação de ciclos de avaliação
                  </CardDescription>
                </div>
                <Button
                  onClick={() => abrirModalTemplate()}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Novo Template
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {templates.length === 0 ? (
                  <div className="col-span-3 text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Carregando templates...</p>
                  </div>
                ) : (
                  templates.map(template => (
                    <Card key={template.id} className="border-2 border-gray-200 hover:border-purple-300 transition-all">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-lg">{template.nome}</CardTitle>
                            <CardDescription>{template.descricao}</CardDescription>
                            {template.is_custom && (
                              <Badge variant="secondary" className="mt-2">
                                <Star className="w-3 h-3 mr-1" />
                                Personalizado
                              </Badge>
                            )}
                          </div>
                          {template.is_custom && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => abrirModalTemplate(template)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeletarTemplate(template.id)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Metas:</span>
                          <span className="font-medium">{template.peso_metas}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Competências:</span>
                          <span className="font-medium">{template.peso_competencias}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Avaliação Gestor:</span>
                          <span className="font-medium">{template.peso_gestor}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Autoavaliação:</span>
                          <span className="font-medium">{template.peso_autoavaliacao}%</span>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-medium text-gray-700 mb-2">
                          Competências ({template.competencias?.length || 0}):
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {(template.competencias || []).map((compId, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {getCompetenciaNome(compId)}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <Button 
                        className="w-full"
                        onClick={() => {
                          aplicarTemplate(template);
                          iniciarWizard('template');
                        }}
                      >
                        <PlayCircle className="w-4 h-4 mr-2" />
                        Usar este Template
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Wizard Modal */}
      {renderWizard()}

      {/* Template Modal */}
      <TemplateModal
        open={modalTemplate}
        onOpenChange={setModalTemplate}
        editingTemplate={editingTemplate}
        novoTemplate={novoTemplate}
        setNovoTemplate={setNovoTemplate}
        competenciasDisponiveis={competencias}  // Nova prop: lista de competências existentes
        loading={loading}
        onSave={editingTemplate ? handleEditarTemplate : handleCriarTemplate}
        onCancel={() => {
          setModalTemplate(false);
          resetTemplateForm();
        }}
      />

      {/* Modal de Detalhes do Ciclo */}
      <Dialog open={modalCicloDetalhes} onOpenChange={setModalCicloDetalhes}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes do Ciclo</DialogTitle>
            <DialogDescription>
              Informações completas do ciclo de avaliação
            </DialogDescription>
          </DialogHeader>
          
          {cicloSelecionado && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm font-semibold text-gray-700">Nome do Ciclo</Label>
                <p className="text-base">{cicloSelecionado.nome || cicloSelecionado.name}</p>
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-gray-700">Descrição</Label>
                <p className="text-base text-gray-600">{cicloSelecionado.descricao || cicloSelecionado.description || 'Sem descrição'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Data Início</Label>
                  <p className="text-base">{cicloSelecionado.data_inicio || cicloSelecionado.start_date}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Data Fim</Label>
                  <p className="text-base">{cicloSelecionado.data_fim || cicloSelecionado.end_date}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Prazo Avaliação</Label>
                  <p className="text-base">{cicloSelecionado.prazo_avaliacao || cicloSelecionado.evaluation_deadline}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Prazo Aprovação</Label>
                  <p className="text-base">{cicloSelecionado.prazo_aprovacao || cicloSelecionado.approval_deadline}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">Distribuição de Pesos</Label>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between p-2 bg-gray-50 rounded">
                    <span>Metas:</span>
                    <span className="font-medium">{cicloSelecionado.peso_metas || 0}%</span>
                  </div>
                  <div className="flex justify-between p-2 bg-gray-50 rounded">
                    <span>Competências:</span>
                    <span className="font-medium">{cicloSelecionado.peso_competencias || 0}%</span>
                  </div>
                  <div className="flex justify-between p-2 bg-gray-50 rounded">
                    <span>Avaliação Gestor:</span>
                    <span className="font-medium">{cicloSelecionado.peso_gestor || 0}%</span>
                  </div>
                  <div className="flex justify-between p-2 bg-gray-50 rounded">
                    <span>Autoavaliação:</span>
                    <span className="font-medium">{cicloSelecionado.peso_autoavaliacao || 0}%</span>
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                  Competências ({cicloSelecionado.competencias?.length || 0})
                </Label>
                <div className="flex flex-wrap gap-2">
                  {(cicloSelecionado.competencias || []).map((compId, idx) => (
                    <Badge key={idx} variant="outline">
                      {getCompetenciaNome(compId)}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Editar Ciclo */}
      <Dialog open={modalEditarCiclo} onOpenChange={setModalEditarCiclo}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Ciclo</DialogTitle>
            <DialogDescription>
              Atualize as informações do ciclo de avaliação
            </DialogDescription>
          </DialogHeader>
          
          {cicloEditando && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="edit-nome">Nome do Ciclo *</Label>
                  <Input
                    id="edit-nome"
                    value={cicloEditando.nome}
                    onChange={(e) => setCicloEditando(prev => ({ ...prev, nome: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-descricao">Descrição</Label>
                  <Textarea
                    id="edit-descricao"
                    value={cicloEditando.descricao}
                    onChange={(e) => setCicloEditando(prev => ({ ...prev, descricao: e.target.value }))}
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-data-inicio">Data Início *</Label>
                  <Input
                    id="edit-data-inicio"
                    type="date"
                    value={cicloEditando.data_inicio}
                    onChange={(e) => setCicloEditando(prev => ({ ...prev, data_inicio: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-data-fim">Data Fim *</Label>
                  <Input
                    id="edit-data-fim"
                    type="date"
                    value={cicloEditando.data_fim}
                    onChange={(e) => setCicloEditando(prev => ({ ...prev, data_fim: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-prazo-avaliacao">Prazo Avaliação *</Label>
                  <Input
                    id="edit-prazo-avaliacao"
                    type="date"
                    value={cicloEditando.prazo_avaliacao}
                    onChange={(e) => setCicloEditando(prev => ({ ...prev, prazo_avaliacao: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-prazo-aprovacao">Prazo Aprovação *</Label>
                  <Input
                    id="edit-prazo-aprovacao"
                    type="date"
                    value={cicloEditando.prazo_aprovacao}
                    onChange={(e) => setCicloEditando(prev => ({ ...prev, prazo_aprovacao: e.target.value }))}
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-semibold mb-2 block">Distribuição de Pesos (%)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-peso-metas" className="text-xs">Peso Metas</Label>
                    <Input
                      id="edit-peso-metas"
                      type="number"
                      min="0"
                      max="100"
                      value={cicloEditando.peso_metas}
                      onChange={(e) => setCicloEditando(prev => ({ 
                        ...prev, 
                        peso_metas: parseInt(e.target.value) || 0,
                        peso_competencias: 100 - parseInt(e.target.value) || 0
                      }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-peso-competencias" className="text-xs">Peso Competências</Label>
                    <Input
                      id="edit-peso-competencias"
                      type="number"
                      value={cicloEditando.peso_competencias}
                      disabled
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setModalEditarCiclo(false);
                    setCicloEditando(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleAtualizarCiclo}
                  disabled={loading}
                >
                  {loading ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AvaliacaoDesempenhoWizard;
